import React from 'react'
import toastr from 'react-hot-toast'
import { useQuery } from 'react-query'
import { fetchWrapper } from 'utils/fetchWrapper'
import { numericSort } from 'utilities/sort'
import { DropdownOption } from 'types/common/DropdownOption'
import { generateURL } from 'utilities/hooks/urlGeneration'
import { SPARK_RESOURCE_TYPES } from 'constants/common'
import { BusinessIndustry, BusinessIndustryResponse } from 'types/Industries'

const getIndustries = () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.BUSINESS_INDUSTRIES)
  return fetchWrapper<BusinessIndustryResponse>({
    url: url,
    method: 'GET',
  })
}

export function useIndustries() {
  const queryResult = useQuery('industries', getIndustries, {
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`Failed to get Industries: ${error.message}`)
      }
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  })

  const { data } = queryResult
  const { data: industries } = data || {}

  const sortedIndustries = industries?.sort((a, b) => {
    return numericSort(a.attributes.sortOrder, b.attributes.sortOrder)
  })

  const dropdownOptions: DropdownOption[] =
    React.useMemo(
      () =>
        sortedIndustries?.map((industry) => ({
          label: industry.attributes.name,
          value: industry.id,
        })),
      [sortedIndustries]
    ) || []

  const idMap = React.useMemo(() => {
    return sortedIndustries?.reduce<Record<string, BusinessIndustry>>(
      (acc, curr) => {
        return { ...acc, [curr.id]: curr }
      },
      {}
    )
  }, [sortedIndustries])

  return {
    dropdownOptions,
    industryIdMap: idMap,
    industries: sortedIndustries,
    ...queryResult,
  }
}
